<script lang="ts">
  import { onMount } from 'svelte';
  import { prefersReducedMotion } from 'svelte/motion';

  interface Props {
    visibles: boolean;
    intensidadBajo?: number;
  }

  let { visibles, intensidadBajo = 0 }: Props = $props();

  interface Luciernaga {
    id: number;
    x: number;
    y: number;
    vx: number;
    vy: number;
    targetX: number;
    targetY: number;
    auraSize: number;
    esPillaPilla: boolean;
    mensaje: string;
    mensajeActivo: boolean;
  }

  let luciernagas = $state<Luciernaga[]>([
    {
      id: 1,
      x: 35,
      y: 60,
      vx: 0.15,
      vy: -0.12,
      targetX: 35,
      targetY: 60,
      auraSize: 22,
      esPillaPilla: true,
      mensaje: '✨ ¡Intenta atraparme!',
      mensajeActivo: true,
    },
    {
      id: 2,
      x: 70,
      y: 65,
      vx: -0.18,
      vy: 0.14,
      targetX: 70,
      targetY: 65,
      auraSize: 18,
      esPillaPilla: false,
      mensaje: '🌸',
      mensajeActivo: false,
    },
    {
      id: 3,
      x: 22,
      y: 72,
      vx: 0.12,
      vy: 0.16,
      targetX: 22,
      targetY: 72,
      auraSize: 20,
      esPillaPilla: false,
      mensaje: '💛',
      mensajeActivo: false,
    },
    {
      id: 4,
      x: 78,
      y: 58,
      vx: -0.14,
      vy: -0.15,
      targetX: 78,
      targetY: 58,
      auraSize: 19,
      esPillaPilla: false,
      mensaje: '🌿',
      mensajeActivo: false,
    },
  ]);

  let animFrameId: number;
  let timerOcultarMensaje: ReturnType<typeof setTimeout> | null = null;
  let timerInactividad15s: ReturnType<typeof setTimeout> | null = null;

  function reiniciarTimerInactividad() {
    if (timerInactividad15s) clearTimeout(timerInactividad15s);

    // Tras 15 segundos continuos sin interactuar ni intentar atraparla
    timerInactividad15s = setTimeout(() => {
      const lider = luciernagas.find((l) => l.esPillaPilla);
      if (lider) {
        lider.mensaje = '✨ ¡Intenta atraparme!';
        lider.mensajeActivo = true;

        if (timerOcultarMensaje) clearTimeout(timerOcultarMensaje);
        // Desaparece tras unos segundos (3.5s)
        timerOcultarMensaje = setTimeout(() => {
          lider.mensajeActivo = false;
        }, 3500);
      }
    }, 15000);
  }

  function mostrarMensajeTemporal(l: Luciernaga, texto: string, duracionMs = 2000) {
    l.mensaje = texto;
    l.mensajeActivo = true;

    if (timerOcultarMensaje) clearTimeout(timerOcultarMensaje);
    timerOcultarMensaje = setTimeout(() => {
      l.mensajeActivo = false;
    }, duracionMs);

    // Reinicia la cuenta regresiva de 15s de inactividad
    reiniciarTimerInactividad();
  }

  function esquivar(l: Luciernaga, cursorX: number, cursorY: number) {
    const ancho = window.innerWidth;
    const alto = window.innerHeight;

    const lxPx = (l.x / 100) * ancho;
    const lyPx = (l.y / 100) * alto;

    const dx = lxPx - cursorX;
    const dy = lyPx - cursorY;
    const dist = Math.hypot(dx, dy);

    // Si el cursor o toque se acerca a menos de 140px
    if (dist < 140 && dist > 0) {
      const fuerza = (140 - dist) / 140;
      const angle = Math.atan2(dy, dx);
      // Salto rápido en dirección opuesta
      l.x += ((Math.cos(angle) * fuerza * 180) / ancho) * 100;
      l.y += ((Math.sin(angle) * fuerza * 180) / alto) * 100;

      // Mantener dentro de la zona de vuelo más baja (52% a 78% de la pantalla)
      l.x = Math.max(10, Math.min(90, l.x));
      l.y = Math.max(52, Math.min(78, l.y));

      if (l.esPillaPilla) {
        mostrarMensajeTemporal(l, '💨 ¡Muy lenta! Jiji', 2000);
      }
    }
  }

  function handlePointer(clientX: number, clientY: number) {
    for (const l of luciernagas) {
      esquivar(l, clientX, clientY);
    }
  }

  function onPointerMove(e: PointerEvent) {
    handlePointer(e.clientX, e.clientY);
  }

  function onTouchMove(e: TouchEvent) {
    const touch = e.touches[0];
    if (touch) {
      handlePointer(touch.clientX, touch.clientY);
    }
  }

  function intentarAtrapar(e: Event, l: Luciernaga) {
    e.stopPropagation();
    // Cuando intenta darle clic/touch, la luciérnaga se escapa instantáneamente a una nueva posición baja
    l.x = Math.random() * 70 + 15;
    l.y = Math.random() * 24 + 52;

    if (l.esPillaPilla) {
      const burla = Math.random() > 0.5 ? '¡Casi! 😜' : '💨 ¡Muy lenta! Jiji';
      mostrarMensajeTemporal(l, burla, 2200);
    }
  }

  // Cada vez que `visibles` pasa a true (al presionar 'Despertar las luciérnagas')
  $effect(() => {
    if (visibles) {
      const lider = luciernagas.find((l) => l.esPillaPilla);
      if (lider) {
        lider.mensaje = '✨ ¡Intenta atraparme!';
        lider.mensajeActivo = true;

        if (timerOcultarMensaje) clearTimeout(timerOcultarMensaje);
        timerOcultarMensaje = setTimeout(() => {
          lider.mensajeActivo = false;
        }, 4000);
      }
      reiniciarTimerInactividad();
    } else {
      if (timerOcultarMensaje) clearTimeout(timerOcultarMensaje);
      if (timerInactividad15s) clearTimeout(timerInactividad15s);
    }
  });

  onMount(() => {
    let t = 0;

    const loop = () => {
      t += 0.02;
      for (const l of luciernagas) {
        // Vuelo orgánico suave
        l.x += Math.sin(t + l.id * 2) * 0.12 + l.vx * 0.2;
        l.y += Math.cos(t + l.id * 1.5) * 0.1 + l.vy * 0.2;

        // Rebote suave en los bordes de la zona baja (52% a 78%)
        if (l.x < 10) { l.x = 10; l.vx = Math.abs(l.vx); }
        if (l.x > 90) { l.x = 90; l.vx = -Math.abs(l.vx); }
        if (l.y < 52) { l.y = 52; l.vy = Math.abs(l.vy); }
        if (l.y > 78) { l.y = 78; l.vy = -Math.abs(l.vy); }
      }
      animFrameId = requestAnimationFrame(loop);
    };

    if (!prefersReducedMotion.current) {
      animFrameId = requestAnimationFrame(loop);
    }

    window.addEventListener('pointermove', onPointerMove);
    window.addEventListener('touchmove', onTouchMove);

    return () => {
      cancelAnimationFrame(animFrameId);
      window.removeEventListener('pointermove', onPointerMove);
      window.removeEventListener('touchmove', onTouchMove);
    };
  });
</script>

{#if visibles}
  <div class="luciernagas-escena" aria-hidden="false">
    {#each luciernagas as l (l.id)}
      <div
        class="luciernaga-viva"
        style="
          left: {l.x}%;
          top: {l.y}%;
          --escala-bajo: {(1 + intensidadBajo * 0.45).toFixed(2)};
        "
        role="button"
        tabindex="0"
        onclick={(e) => intentarAtrapar(e, l)}
        onpointerdown={(e) => intentarAtrapar(e, l)}
        onkeydown={(e) => e.key === 'Enter' && intentarAtrapar(e, l)}
        aria-label="Luciérnaga juguetona"
      >
        <!-- Núcleo de luz viva -->
        <span class="luz-nucleo"></span>
        <span class="luz-halo" style="width: {l.auraSize}px; height: {l.auraSize}px;"></span>

        <!-- Mensaje de la luciérnaga líder -->
        {#if l.mensajeActivo}
          <div class="globo-luciernaga" aria-live="polite">
            {l.mensaje}
          </div>
        {/if}
      </div>
    {/each}
  </div>
{/if}

<style>
  .luciernagas-escena {
    position: fixed;
    inset: 0;
    pointer-events: none; /* las luciérnagas individuales activan pointer-events */
    z-index: 25; /* situadas encima del fondo y el prado, accesibles al tacto */
    overflow: hidden;
  }

  .luciernaga-viva {
    position: absolute;
    transform: translate3d(-50%, -50%, 0) scale(var(--escala-bajo));
    pointer-events: auto;
    cursor: pointer;
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    padding: 18px; /* hit area generosa para touch */
    will-change: transform, left, top;
    transition:
      transform 0.12s ease-out,
      left 0.35s cubic-bezier(0.2, 0.8, 0.3, 1),
      top 0.35s cubic-bezier(0.2, 0.8, 0.3, 1);
    -webkit-tap-highlight-color: transparent;
  }

  .luz-nucleo {
    width: 6px;
    height: 6px;
    border-radius: 50%;
    background: #ffffff;
    box-shadow:
      0 0 10px #ffe57f,
      0 0 20px #ffc107,
      0 0 35px #ffa000;
    animation: pulsoVivo 1.8s ease-in-out infinite alternate;
  }

  .luz-halo {
    position: absolute;
    border-radius: 50%;
    background: radial-gradient(circle, rgba(255, 235, 130, 0.45) 0%, transparent 75%);
    filter: blur(4px);
    pointer-events: none;
    animation: haloRespirar 2.4s ease-in-out infinite alternate;
  }

  @keyframes pulsoVivo {
    0% {
      transform: scale(0.85);
      opacity: 0.7;
    }
    100% {
      transform: scale(1.3);
      opacity: 1;
    }
  }

  @keyframes haloRespirar {
    0% {
      transform: scale(0.9);
      opacity: 0.4;
    }
    100% {
      transform: scale(1.4);
      opacity: 0.9;
    }
  }

  .globo-luciernaga {
    position: absolute;
    bottom: 28px;
    background: rgba(26, 34, 56, 0.9);
    border: 1px solid rgba(255, 235, 150, 0.5);
    color: #fff8eb;
    font-family: var(--familia-tipografica);
    font-size: 0.78rem;
    padding: 4px 10px;
    border-radius: 12px;
    white-space: nowrap;
    box-shadow: 0 4px 14px rgba(0, 0, 0, 0.4);
    backdrop-filter: blur(6px);
    -webkit-backdrop-filter: blur(6px);
    pointer-events: none;
    user-select: none;
    animation: flotarGlobo 2.5s ease-in-out infinite alternate;
  }

  @keyframes flotarGlobo {
    0% {
      transform: translateY(0);
    }
    100% {
      transform: translateY(-4px);
    }
  }
</style>
